/**
 * Klaviyo Back In Stock Integration (Email + SMS)
 * Web Component Version - Final Specifications 2026
 */
class NotifyMeButton extends HTMLElement {
  constructor() {
    super();
    // Klaviyo API Credentials and Versioning
    this.companyId = "TwyLiz";
    this.revision = "2024-06-15";

    // Select internal DOM elements within the component
    this.buttonTrigger = this.querySelector('.notify-me-button-trigger');
    this.popup = this.querySelector('.notify-me-popup');
    this.closeBtn = this.querySelector('.notify-me-popup__close');
    this.submitBtn = this.querySelector('.notify-me-popup__button');
    this.emailInput = this.querySelector('.notify-me-popup__input-email');
    this.phoneInput = this.querySelector('.notify-me-popup__input-phone');
    this.variantSelect = this.querySelector('.notify-me-popup__variant-select');
    this.variantDisplay = this.querySelector('.notify-me-popup__variant');

    // Retrieve configuration and messages from data attributes
    this.successMessage = this.getAttribute('data-success-message');
    this.invalidMessage = this.getAttribute('data-invalid-message');

    // Check if the component should be active specifically for this product
    this.isApplyOnlyForThisProduct = this.getAttribute('data-enable-locally-product') === 'true';

    // Element used to display validation errors or success messages
    this.messageBlock = this.querySelector('.notify-me-popup__message');
  }

  /**
   * Updates the UI to show the current selected variant title
   */
  updateVariantName() {
    if (!this.variantDisplay || !this.variantSelect) return;

    const currentVariantId = String(window.bicConfig?.variant_id);
    const options = Array.from(this.variantSelect.options);
    const currentOption = options.find(opt => opt.value === currentVariantId);

    if (currentOption) {
      this.variantDisplay.innerText = currentOption.textContent.trim();
    }
  }

  /**
   * Determines if the "Notify Me" button should be visible based on stock and settings
   * Priority: Local Override > Global Config > Product Level Enablement
   */
  isGloballyEnabled() {
    const isLocallyOutOfStock = Boolean(document.querySelector('[data-force-local-oos="true"]'));

    if (isLocallyOutOfStock) {
      document.body.classList.remove("show-notify-me-button", "show-notify-me-popup", "notify-popup-custom");
      return false;
    }

    // Check for hard-coded local override (force show)
    const isLocallyEnabled = this.getAttribute('data-locally') === 'true';
    const isDisabled = (window.disabledVariantIds || '')
      .split(',')
      .includes(String(window.bicConfig?.variant_id));


    if (isLocallyEnabled || isDisabled) {
      document.body.classList.add("show-notify-me-button");
      this.updateVariantName();
      return true;
    }

    // Check Klaviyo global settings and per-product local enablement
    const isEnabledGlobally = window.bicConfig?.enable_notify_me_globally === 'true';
    const shouldRunStockCheck = isEnabledGlobally || this.isApplyOnlyForThisProduct;

    if (shouldRunStockCheck) {
      const currentVariantId = String(window.bicConfig?.variant_id);
      const quantityTrigger = parseInt(window.bicConfig?.notify_me_quantity_trigger) || 0;

      if (this.variantSelect) {
        const options = Array.from(this.variantSelect.options);
        const currentOption = options.find(opt => opt.value === currentVariantId);

        if (currentOption) {
          const variantQty = parseInt(currentOption.getAttribute('data-variant-qty')) || 0;

          // Only show the button if inventory is below or equal to the trigger threshold
          if (variantQty <= quantityTrigger) {
            this.updateVariantName();
            document.body.classList.add("show-notify-me-button");
            return true;
          }
        }
      }
    }

    // Hide button if conditions are not met
    document.body.classList.remove("show-notify-me-button");
    return false;
  }

  /**
   * Toggles the visibility of the popup and resets message states
   */
  togglePopup(show) {
    if (this.messageBlock) this.messageBlock.innerText = "";

    if (show) {
      this.updateVariantName();
      document.body.classList.add("show-notify-me-popup", "notify-popup-custom");
    } else {
      document.body.classList.remove("show-notify-me-popup", "notify-popup-custom");
    }
  }

  /**
   * Handles form validation and Klaviyo API submission
   */
  async handleSubmit(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    const email = this.emailInput?.value.trim();
    const phone = this.phoneInput?.value.trim();

    // Reset previous error messages
    if (this.messageBlock) this.messageBlock.innerText = "";

    // Basic Validation: Email must contain @
    const isEmailValid = email && email.includes('@');

    // Phone Validation: Remove + and spaces, then check for non-digit characters
    const isPhoneValid = phone !== ""; // Simple check: is the field not empty?

    if (!isEmailValid || !isPhoneValid) {
      if (this.messageBlock) {
        this.messageBlock.innerText = this.invalidMessage;
      }
      return;
    }

    const variantId = window.bicConfig?.variant_id;
    if (!variantId) return;

    this.setLoading(true);
    const countryCodeNumber = document.querySelector('.iti__selected-dial-code');
    let formattedPhone = phone.replace(/[\s\(\)\-]/g, '');

    if (countryCodeNumber) {
      formattedPhone = (countryCodeNumber.innerText.trim() + formattedPhone).trim();
    }

    // Prepare Klaviyo API v2024-06-15 Payload
    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                ...(phone && { phone_number: formattedPhone }) // Add phone only if provided
              }
            }
          },
          channels: phone ? ["EMAIL", "SMS"] : ["EMAIL"]
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variantId}`
            }
          }
        }
      }
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "revision": this.revision
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.status === 202) {
        if (this.messageBlock) {
          this.messageBlock.innerText = this.successMessage;
        }
      } else {
        const errorData = await response.json();
        const detail = errorData.errors?.[0]?.detail || "Unknown error";
        alert(`Error: ${detail}`);
      }
    } catch (error) {
      console.error("Network Error:", error);
      alert("Network error. Please try again.");
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Sets the visual loading state of the submit button
   */
  setLoading(isLoading) {
    if (isLoading) {
      this.submitBtn.disabled = true;
      this._originalText = this.submitBtn.innerText;
      this.submitBtn.innerText = "Processing...";
    } else {
      this.submitBtn.disabled = false;
      this.submitBtn.innerText = this._originalText;
    }
  }

  /**
   * Web Component lifecycle method called when element is added to DOM
   */
  connectedCallback() {
    // Expose instance globally for theme-level interactions (e.g., variant switchers)
    window.NotifyMe = this;

    // Initial stock/setting check
    this.isGloballyEnabled();

    // Event Listeners
    this.buttonTrigger?.addEventListener('click', () => {
      if (this.isGloballyEnabled()) this.togglePopup(true);
    });

    // Close popup on overlay click or close button click
    this.addEventListener('click', (e) => {
      if (e.target === this.popup || e.target === this.closeBtn) {
        this.togglePopup(false);
      }
    });

    // Handle form submission
    this.submitBtn?.addEventListener('click', (e) => this.handleSubmit(e));

    // Phone input library call
    window.intlTelInput(this.phoneInput, {
      loadUtils: () => import("https://cdn.jsdelivr.net/npm/intl-tel-input@26.9.2/build/js/utils.js"),
      "initialCountry": "us",
      "separateDialCode": true
    });
  }
}

// Register the custom element if not already registered
if (!customElements.get('notify-me-button')) {
  customElements.define('notify-me-button', NotifyMeButton);
}